import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import StoryCard, { StorySkeleton } from './StoryCard'
import styles from './HeaderPanels.module.css'
import { coverageHref } from 'lib/editorial/taxonomy'
import { withSlash } from 'lib/dealhunt/url'
import { useCatalogEvent } from 'lib/analytics/catalog'
import {
  CATEGORY_STORIES,
  coverageHubsFor,
  viewAllLabel,
  type HeaderTab,
} from 'lib/header-nav/config'
import type { HeaderNavTab } from 'lib/header-nav/types'

interface CategoryPanelProps {
  /** Always present — labels and links come with the page (common data), never from the fetch. */
  config: HeaderTab
  /** Absent until `/api/header-nav` resolves, or if it failed. */
  data: HeaderNavTab | null
  /** True while the document is still being fetched — drives the skeletons. */
  loading: boolean
  /** Catalogue link for this tab, built by the header so it can't drift from the tab. */
  fallbackHref: string
  trailingSlash: boolean
  onNavigate: () => void
}

/** Browse children vary per tab (3-5); four placeholder rows splits the difference. */
const SKELETON_BROWSE_ROWS = 4

/** The compare page only handles these form factors (`FormFactor` in `lib/dealhunt/compareMatch`). */
const COMPARE_TABS: ReadonlySet<string> = new Set(['earbuds', 'headphones'])

/**
 * One category tab's panel: Browse (catalogue children) · Coverage (editorial hubs scoped
 * to this category) · Latest.
 *
 * STRUCTURE FIRST, NUMBERS WHEN THEY LAND. Everything that can be known without the
 * network — column headings, every Coverage row and its destination, the catalogue link —
 * renders immediately from config. Counts and stories fill in when the document arrives.
 *
 * The earlier version gated all three columns on `data`, so opening a tab before the
 * fetch resolved showed an empty box for as long as the request took. A panel that opens
 * instantly and gains numbers a moment later reads as fast; one that opens blank reads as
 * broken, even when it is the same wait. Skeletons finish the idea: while the document is
 * in flight, the doc-fed parts (browse rows, counts, stories) pulse in place instead of
 * popping in, so the first-visit half-second reads as loading, not as a jump.
 */
const CategoryPanel: React.VFC<CategoryPanelProps> = ({
  config,
  data,
  loading,
  fallbackHref,
  trailingSlash,
  onNavigate,
}) => {
  const { short } = config
  const catalogueHref = data?.href ?? fallbackHref
  const { track } = useCatalogEvent()
  const compareHref = withSlash('/compare', trailingSlash)
  const compareLabel = `Compare ${config.label}`

  return (
    <div className={styles.inner}>
      <div className={styles.columns}>
        <div className={styles.column}>
          <div className={styles.columnHead}>
            <span className={styles.columnTitle}>{`Browse ${short}`}</span>
          </div>
          {data?.children.map(child => (
            <Linked key={child.href} href={child.href} className={styles.row} onClick={onNavigate}>
              <span>{child.label}</span>
              {child.count !== null && <span className={styles.rowCount}>{child.count}</span>}
            </Linked>
          ))}
          {!data &&
            loading &&
            Array.from({ length: SKELETON_BROWSE_ROWS }, (_, i) => (
              <span key={i} className={styles.row} aria-hidden="true">
                <span className={clsx(styles.skeleton, styles.skeletonRowLabel)} />
                <span className={clsx(styles.skeleton, styles.skeletonRowCount)} />
              </span>
            ))}
          <Linked href={catalogueHref} className={styles.viewAll} onClick={onNavigate}>
            {viewAllLabel(data?.productTotal ?? null, short)}
          </Linked>
        </div>

        <div className={styles.column}>
          <div className={styles.columnHead}>
            <span className={styles.columnTitle}>{'Coverage'}</span>
          </div>
          {coverageHubsFor(config).map(hub => {
            // The count may not have arrived yet, but the destination is knowable now —
            // it's the hub's own coverage page for this scope.
            const row = data?.coverage.find(r => r.id === hub.id)
            return (
              <Linked
                key={hub.id}
                href={coverageHref(hub.path, config.id, trailingSlash)}
                className={styles.row}
                onClick={onNavigate}
              >
                <span>{hub.label}</span>
                {row?.count !== null && row?.count !== undefined && (
                  <span className={styles.rowCount}>{row.count}</span>
                )}
                {!data && loading && (
                  <span
                    className={clsx(styles.skeleton, styles.skeletonRowCount)}
                    aria-hidden="true"
                  />
                )}
              </Linked>
            )
          })}
          <p className={styles.note}>{`Every editorial hub, scoped to ${short}.`}</p>

          {COMPARE_TABS.has(config.id) && (
            <>
              <div className={clsx(styles.columnHead, styles.columnHeadStacked)}>
                <span className={styles.columnTitle}>{'Tools'}</span>
              </div>
              <Linked
                href={compareHref}
                className={styles.row}
                onClick={() => {
                  track('product_search_header_compare_click', {
                    scope: config.id,
                    label: compareLabel,
                    destination_url: compareHref,
                  })
                  onNavigate()
                }}
              >
                <span>{compareLabel}</span>
              </Linked>
            </>
          )}
        </div>

        {(data ? data.stories.length > 0 : loading) && (
          <div className={styles.column}>
            <div className={styles.columnHead}>
              <span className={styles.columnTitle}>{`Latest in ${short}`}</span>
            </div>
            {data
              ? data.stories.map(story => (
                  <StoryCard
                    key={story.url}
                    story={story}
                    meta="category"
                    onNavigate={onNavigate}
                  />
                ))
              : Array.from({ length: CATEGORY_STORIES }, (_, i) => <StorySkeleton key={i} />)}
          </div>
        )}
      </div>
    </div>
  )
}

export default CategoryPanel
